import { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import ChatBubble from './ChatBubble';
import StatusLine from './StatusLine';
import { ChatMessageResponse, Citation } from '../../services/chatbot.service';

interface ChatMessageListProps {
  messages: ChatMessageResponse[];
  /** Texto de la línea de estado mientras se responde (utils/statusLine.ts). */
  statusLabel: string;
  streamingContent: string;
  streamingCitations: Citation[];
  isStreaming: boolean;
  onDocumentClick: (citation: Citation, messageCitations: Citation[]) => void;
  onSuggestionClick: (suggestion: string) => void;
  /** Motivo del último fallo; se borra al enviar otra consulta. */
  error?: string | null;
}

export default function ChatMessageList({
  messages, statusLabel, streamingContent, streamingCitations,
  isStreaming, onDocumentClick, onSuggestionClick, error
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // El historial trae los pasos intermedios del agente (llamadas a herramientas) como mensajes del asistente
  // sin texto; no se pintan, o cada uno deja una etiqueta «AlertAI» vacía sobre la respuesta.
  const visible = messages.filter(m => m.role !== 'assistant' || m.content?.trim() || (m.citations?.length ?? 0) > 0);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    bottomRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
  }, [messages.length, streamingContent, statusLabel, isStreaming, error]);

  return (
    <div className="flex-1 overflow-y-auto px-6 py-6">
      <div data-chat-column className="max-w-3xl mx-auto flex flex-col justify-end min-h-full">
        <div className="space-y-1">
          {visible.map((msg, idx) => (
            <div key={msg.id}>
              {/* Al terminar, «modo · N fuentes · N s» en el lugar de la línea de estado. Solo en turnos enviados en
                  esta visita; el historial recargado no la trae. */}
              {msg.role === 'assistant' && msg.reasoningReceipt && (
                <p className="text-[11px] text-slate-500 mb-1 tabular-nums">{msg.reasoningReceipt}</p>
              )}
              <ChatBubble
                id={msg.id}
                role={msg.role}
                content={msg.content}
                citations={msg.citations}
                question={msg.role === 'assistant' && visible[idx - 1]?.role === 'user' ? visible[idx - 1].content : undefined}
                onDocumentClick={onDocumentClick}
                onSuggestionClick={!isStreaming && idx === visible.length - 1 ? onSuggestionClick : undefined}
              />
            </div>
          ))}

          {/* Línea de estado: sigue las etapas del motor (o la herramienta en curso) hasta que termina el turno. */}
          {isStreaming && <StatusLine label={statusLabel} />}

          {/* Streaming assistant message */}
          {isStreaming && streamingContent && (
            <ChatBubble
              role="assistant"
              content={streamingContent}
              citations={streamingCitations}
              onDocumentClick={onDocumentClick}
              isStreaming={true}
            />
          )}

          {/* Error (DESIGN.md §4): una línea gris con el motivo, en la columna de la respuesta; sin franja de color. */}
          {error && !isStreaming && (
            <p role="alert" className="flex items-center gap-2 text-[13px] text-slate-500 mb-3">
              <AlertCircle className="w-3.5 h-3.5 text-slate-400 flex-none" strokeWidth={1.8} aria-hidden="true" />
              {error}
            </p>
          )}
        </div>
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
